import {useMachine} from '@xstate/react';
import axios from 'axios';
import _, {get, head, isEqual, pick} from 'lodash';
import React, {useCallback, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {Machine, assign} from 'xstate';

import {createEndpointHook} from './util/endpointhooks.ts';

import {toast} from '../ui-toolkit';
import {ScopedPreferences} from './PreferencesContext';

export const LoginStatus = {
    Active: 'Active',
    Pending: 'Pending',
    Blocked: 'Blocked',
};

export const AdminAccessRole = {
    SuperAdmin: 'SuperAdmin',
    Admin: 'Admin',
    ReadOnlyAdmin: 'ReadOnlyAdmin',
    User: 'User',
};

// we get a lot of properties from the server (e.g. the JWT tokens) that we don't need to keep stored in the auth context
// the list below contains the properties that we do want to save
const savedUserProperties = [
    'applicationRoleId',
    'displayName',
    'primaryLocale',
    'role',
    'userId',
    'availableRoles',
    'email',
];

const savedApplicationProperties = ['domainName', 'name', 'primaryLocale'];

export function getApplicationFromDomain() {
    return axios.get('/api/user-access/application/fromDomain').then((response) => response.data);
}

export function getAccountSettings() {
    return axios.get(`/api/user-access/account-settings`);
}

//#region Basic Login Actions

function logIn({username, password, totp}) {
    return axios
        .post(`/api/user-access/authenticate`, {
            username,
            password,
            totp,
        })
        .then((response) => {
            return response.data;
        });
}

function logOut() {
    return axios.post('/api/user-access/logout');
}

export function refreshToken(applicationRoleId) {
    return axios
        .post('/api/user-access/refresh-token', {
            applicationRoleId,
        })
        .then((response) => {
            return response.data;
        });
}

//#endregion

//#region Email/Password Login

export function resendActivation(userId, emailAddress) {
    return axios.post(`/api/user-access/users/${userId}/resend-activation-email/${emailAddress}`);
}

export function addEmailPasswordLogin(userId, emailAddress) {
    return axios.post(`/api/user-access/users/${userId}/add-password-login/${emailAddress}`);
}

export function deletePasswordLoginForUser(userId, data) {
    return axios.post(`/api/user-access/users/${userId}/delete-password-login`, data);
}

export function activatePassword(values) {
    return axios.post(`/api/user-access/activate`, values);
}

export function resetPassword(values) {
    return axios.post(`/api/user-access/resetpassword`, values);
}

export function triggerPasswordReset({username, totp}) {
    return axios.post(`/api/user-access/forgotpassword`, {
        username,
        totp,
    });
}

//#endregion

//#region API key Login

export function addApiKey(userId) {
    return axios.post(`/api/user-access/users/${userId}/add-api-key`);
}

export function deleteApiLoginForUser(userId, data) {
    return axios.post(`/api/user-access/users/${userId}/delete-api-login`, data);
}

//#endregion

//#region SSO Login

/**
 * Requests the URL to start the SSO login process.
 *
 * @param {number} id The id of the sso provider.
 *
 * @returns {Promise<import('axios').AxiosResponse<{url: string}>>}
 */
export function initializeSsoLogin(id) {
    return axios.get(`/api/user-access/sso/${id}/login`);
}

/**
 * Requests the URL to start the SSO linking process as well as the SSO provider's name.
 *
 * @param {string} token The paseto token.
 *
 * @returns {Promise<{provider: string; url: string}>}
 */
export function getSsoTokenInfo(token) {
    return axios.get(`/api/user-access/sso/token-info/${token}`).then((response) => response.data);
}

/**
 * Requests the URL to start the SSO linking process as well as the SSO provider's name.
 */
export const useSsoTokenInfo = createEndpointHook(getSsoTokenInfo);

/**
 * Starts the process for linking a new SSO login to the currently logged in user.
 *
 * @param {number} id The id of the sso provider.
 *
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export function linkSsoLogin(id) {
    return axios.post(`/api/user-access/sso/link`, {
        ssoConfigurationId: id,
    });
}

/**
 * Starts the process for linking a new SSO login to the selected user.
 *
 * @param {number} id The id of the sso provider.
 * @param {string} email The email used to send the SSO link.
 *
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export function addSsoLoginForUser(userId, formValues) {
    return axios.post(`/api/user-access/users/${userId}/add-sso-login`, {
        ssoConfigurationId: formValues.ssoConfigurationId,
        emailAddress: formValues.emailAddress,
    });
}

/**
 * Deletes an SSO login for the currently logged in user.
 *
 * @param {number} id The id of the sso provider.
 *
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export function deleteSsoLogin(id) {
    return axios.delete(`/api/user-access/sso/${id}`);
}

/**
 * Deletes an SSO login for the selected user.
 *
 * @param {string} userId The id of the selected user.
 * @param {number} id The id of the sso provider.
 *
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export function deleteSsoLoginForUser(userId, id) {
    return axios.delete(`/api/user-access/sso/${userId}/${id}`);
}

//#endregion

//#region 2FA

export function getTotpSecret(totpSecret, totpEmail) {
    return axios.get(`/api/user-access/totp-secret/${totpEmail}`, {params: {totpSecret: totpSecret}});
}

export function enableTotpForLogin({username, password}) {
    return axios.post(`/api/user-access/enable-totp-for-login`, {username, password});
}

export function enableTotp(totpEmail) {
    return axios.post(`/api/user-access/enable-totp/${totpEmail}`);
}

export function disableTotp(totpSecret, totpEmail) {
    return axios.post(`/api/user-access/disable-totp`, {totpSecret, totpEmail});
}

export function totpQrUrl(useremail, secret) {
    return `otpauth://totp/Simba:${useremail}?secret=${secret}`;
}

export function triggerTwoFactorReset({username, password}) {
    return axios.post(`/api/user-access/forgottwofactor`, {
        username,
        password,
    });
}

export function resetTotp(values) {
    return axios.post(`/api/user-access/lost-totp`, values);
}

//#endregion

export const authStateMachine = Machine({
    id: 'authstate',
    initial: 'initial',
    context: {
        currentUser: null,
        application: null,
        applicationSettings: null,
        errorMessage: null,
        authToken: null,
        ssoProviders: null,
    },
    states: {
        initial: {
            on: {
                INITIAL_LOAD: 'initial_load',
            },
        },

        initial_load: {
            invoke: {
                id: 'initial_load',
                src: () => {
                    return getApplicationFromDomain();
                },
                onDone: {
                    target: 'refresh_token',
                    actions: assign({
                        applicationSettings: (ctx, event) => event.data.settings,
                        application: (ctx, event) => pick(event.data, savedApplicationProperties),
                        ssoProviders: (ctx, event) => event.data.ssoProviders,
                    }),
                },
                onError: {
                    target: '#error',
                    actions: assign({
                        errorMessage: (ctx, event) => {
                            let serverResponse = get(event, 'data.response.status', null);

                            if (serverResponse === 404) {
                                return 'loading.invalidDomain';
                            } else {
                                return 'loading.loadingError';
                            }
                        },
                    }),
                },
            },
        },

        refresh_token: {
            invoke: {
                id: 'refresh_token',
                src: () => {
                    return refreshToken();
                },
                onDone: {
                    target: 'signed_in',
                    actions: assign({
                        currentUser: (ctx, event) => pick(event.data, savedUserProperties),
                        authToken: (ctx, event) => event.data.token,
                        errorMessage: () => null,
                    }),
                },
                onError: {
                    target: '#signed_out',
                    actions: assign({
                        currentUser: () => null,
                        errorMessage: (_, event) => {
                            const raw = get(event, 'data.response.data.message');
                            if (!raw || raw === 'auth.token.missing') {
                                // If the auth token is missing during this initial refresh step, that means this is a
                                // "fresh" visit to the site, ie. no cookies were found. In this case, we don't want to
                                // show an error message.
                                return null;
                            }

                            const message = raw.replaceAll('/', '.');
                            return message.startsWith('auth.') ? message : `auth.${message}`;
                        },
                    }),
                },
            },
        },

        signed_in: {
            id: 'signed_in',
            initial: 'signed_in',
            states: {
                signed_in: {
                    on: {
                        SIGN_OUT: '#signed_out',
                        REFRESH: 'refreshing',
                        INIT_ROLE_SWITCH: '#role_switch',
                    },
                    after: {
                        // every two minutes refresh the JWT
                        120000: 'refreshing',
                    },
                },
                refreshing: {
                    invoke: {
                        src: () => {
                            return refreshToken();
                        },
                        onDone: {
                            target: 'signed_in',
                            actions: assign({
                                currentUser: (ctx, event) => pick(event.data, savedUserProperties),
                                authToken: (ctx, event) => event.data.token,
                                errorMessage: () => null,
                            }),
                        },
                        onError: [
                            {
                                target: '#signed_out',
                                actions: assign({
                                    currentUser: () => null,
                                    authToken: () => null,
                                    errorMessage: (_, event) => {
                                        const message = get(
                                            event,
                                            'data.response.data.message',
                                            'auth.token.invalid',
                                        ).replaceAll('/', '.');

                                        return message.startsWith('auth.') ? message : `auth.${message}`;
                                    },
                                }),
                                cond: (ctx, event) => {
                                    let httpStatus = get(event, 'data.response.status');
                                    return httpStatus === 401;
                                },
                            },
                            {
                                // stay in signed-in state if it's not a 401, to prevent being logged out
                                // in case of transient server errors
                                target: 'signed_in',
                            },
                        ],
                    },
                },
            },
        },

        role_switch: {
            id: 'role_switch',
            initial: 'role_switch',
            states: {
                role_switch: {
                    on: {
                        DONE_ROLE_SWITCH: {
                            target: '#signed_in',
                            actions: assign({
                                currentUser: (ctx, event) => pick(event.data, savedUserProperties),
                                authToken: (ctx, event) => event.data.token,
                                errorMessage: () => null,
                            }),
                        },
                    },
                },
            },
        },

        signed_out: {
            id: 'signed_out',
            on: {
                SIGN_IN: 'signing_in',
            },
            entry: () => {
                // when entering the "signed out" state, make the server delete the refresh token cookie
                logOut();
            },
        },

        signing_in: {
            on: {
                SIGNED_IN: {
                    target: 'signed_in',
                    actions: assign({
                        currentUser: (ctx, event) => event.data,
                        authToken: (ctx, event) => event.data.token,
                        errorMessage: null,
                    }),
                },
                SIGNIN_FAILURE: 'signed_out',
            },
        },

        error: {
            id: 'error',
        },
    },
});

const AuthContext = React.createContext(null);
export const JwtContext = React.createContext(null);

// the auth values change every time the token is refreshed, which leads to an annoying re-render
// we use memoisation here to make sure we only render when something actually changed
const MemoizedProvider = React.memo(AuthContext.Provider, isEqual);

export function WithAuthorisation(props) {
    const [authState, updateAuthState] = useMachine(authStateMachine);
    const {i18n} = useTranslation();

    const signOut = useCallback(() => {
        updateAuthState('SIGN_OUT');
    }, [updateAuthState]);

    // try to log in based on refresh cookie
    updateAuthState('INITIAL_LOAD');

    const performLogin = useCallback(
        (params) => {
            updateAuthState('SIGN_IN');
            return logIn(params)
                .then((res) => {
                    updateAuthState({type: 'SIGNED_IN', data: res});
                    return {success: true, errors: null};
                })
                .catch((err) => {
                    let errors = null;
                    if (get(err, 'response.status') === 400) {
                        errors = err.response.data;
                    } else {
                        errors = {
                            username: 'errors.general',
                        };
                    }
                    updateAuthState({type: 'SIGNIN_FAILURE'});
                    return {success: false, errors};
                });
        },
        [updateAuthState],
    );

    const switchRole = useCallback(
        (roleId) => {
            updateAuthState('INIT_ROLE_SWITCH');
            return refreshToken(roleId).then((res) => {
                updateAuthState({type: 'DONE_ROLE_SWITCH', data: res});
                return {success: true, errors: null};
            });
        },
        [updateAuthState],
    );

    // Note: isLoggedIn and isNotLoggedIn are not opposites!
    // There is also the initial state in which we don't know (both are false)
    const isLoggedIn = head(authState.toStrings()) === 'signed_in';
    const isNotLoggedIn = head(authState.toStrings()) === 'signed_out' || head(authState.toStrings()) === 'signing_in';

    // set the user language when it changes
    const userLocale = get(authState, 'context.currentUser.primaryLocale');
    const applicationLocale = get(authState, 'context.application.primaryLocale');
    useEffect(() => {
        if (applicationLocale && !userLocale) i18n.changeLanguage(applicationLocale);
        if (userLocale) i18n.changeLanguage(userLocale);
    }, [userLocale, applicationLocale, i18n]);

    const errorMessage = get(authState, 'context.errorMessage');
    useEffect(() => {
        if (!errorMessage) return;
        toast.error(i18n.t([errorMessage, 'errors.general']));
    }, [errorMessage, i18n]);

    const providerValue = {
        currentUser: authState.context.currentUser,
        applicationSettings: authState.context.applicationSettings,
        application: authState.context.application,
        errorMessage: authState.context.errorMessage,
        ssoProviders: authState.context.ssoProviders,
        logIn: performLogin,
        isLoggedIn,
        isNotLoggedIn,
        signOut,
        resendActivation,
        triggerPasswordReset,
        switchRole,
        error: head(authState.toStrings()) === 'error',
    };

    const authToken = authState.context.authToken;

    const userId = _.get(providerValue, 'currentUser.userId', 'none');
    const userRole = _.get(providerValue, 'currentUser.applicationRoleId', 'none');

    return (
        <ScopedPreferences name={userId}>
            <ScopedPreferences name={'role-' + userRole}>
                <JwtContext.Provider value={authToken}>
                    <MemoizedProvider value={providerValue}>{props.children}</MemoizedProvider>
                </JwtContext.Provider>
            </ScopedPreferences>
        </ScopedPreferences>
    );
}

export default AuthContext;
